import { useMutation } from '@apollo/client';

import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';
import type { CoursePlanDialogType } from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/context/CoursePlanningDialogContext';
import { moveCourseToTerm } from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/lib/coursePlanningUtils';
import type {
  UseCurriculum_PersonalizedSegmentCourseFragment as PersonalizedSegmentCourse,
  UseCurriculum_PersonalizedSegmentCourseFragment as SegmentCourse,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import type {
  LearnerPlan_MoveCourseMutation as MoveCourseMutationData,
  LearnerPlan_MoveCourseMutationVariables as MoveCourseMutationVariables,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/moveCoursesMutation';
import type { UseDegreePlanQuery as DegreePlanData } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/useDegreePlan';
import LearnerPlanMoveCourseMutation from 'bundles/degree-home/components/v3-dpm/lib/__queries__/moveCoursesMutation.graphql';

import useDegreePlan from './useDegreePlan';

export type MoveCourseInputType = {
  destinationTermId: string;
  sourceTermId: string;
  selectedCourse: PersonalizedSegmentCourse;
  version: number;
  degreePlanData: DegreePlanData;
};

export default function useMoveCourseMutation() {
  const { updateQuery } = useDegreePlan();
  const [moveCourseMutation, mutationState] = useMutation<MoveCourseMutationData, MoveCourseMutationVariables>(
    LearnerPlanMoveCourseMutation,
    { context: { clientName: 'gatewayGql' } }
  );
  const { degreeId } = useDegreeContext();

  const moveCourse = async ({
    openCourseDialog,
    destinationTermId,
    sourceTermId,
    selectedCourse,
    version,
    degreePlanData,
  }: {
    openCourseDialog: (course: SegmentCourse | null, dialogType: CoursePlanDialogType) => void;
  } & MoveCourseInputType) => {
    try {
      const learnerPlan = degreePlanData?.LearnerPlan?.queryPlan?.learnerPlan || [];
      const optimisticResponse = moveCourseToTerm(learnerPlan, destinationTermId, sourceTermId, selectedCourse);

      const res = await moveCourseMutation({
        variables: {
          input: {
            courseId: selectedCourse.segmentCourse.id,
            degreeId,
            destinationTermId,
            sourceTermId,
            version,
          },
        },
        optimisticResponse: {
          /* eslint-disable camelcase */
          LearnerPlan_MoveCourse: {
            __typename: 'LearnerPlan_LearnerPlanResult',
            learnerPlan: optimisticResponse,
            version,
          },
        },
      });

      const mutationResult = res.data?.LearnerPlan_MoveCourse;

      if (mutationResult?.__typename !== 'LearnerPlan_LearnerPlanResult' || mutationState.error || res.errors?.length) {
        openCourseDialog(null, 'error');
        return false;
      }

      updateQuery(() => {
        return {
          __typename: 'Query',
          LearnerPlan: {
            __typename: 'LearnerPlanQueries',
            queryPlan: mutationResult,
          },
        };
      });
      return mutationResult;
    } catch {
      openCourseDialog(null, 'error');
      return false;
    }
  };

  return {
    moveCourse,
    moveCoursePlanLoading: mutationState.loading,
  };
}
